import React from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Collapse,
  IconButton,
  Alert,
} from '@mui/material';
import { Plus, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Dayjs } from 'dayjs';
import SubtaskForm from './SubtaskForm';
import type { User as UserType } from '@/types';
import { formatPercent } from '@/shared/utils/formatPercentage';

export interface SubtaskData {
  title: string;
  estimatedTime: number; // minutes
  dueDate: Dayjs | null;
  responsible: string[];
}

interface SubtasksListProps {
  subtasks: SubtaskData[];
  onSubtaskChange: (index: number, data: SubtaskData) => void;
  onSubtaskAdd: () => void;
  onSubtaskRemove: (index: number) => void;
  parentEstimatedTime: number; // minutes
  parentDueDate: Dayjs;
  parentStartDate: Dayjs | null;
  availableUsers: UserType[]; // RACI pool from parent
  errors?: Record<number, {
    title?: string;
    estimatedTime?: string;
    dueDate?: string;
    responsible?: string;
  }>;
  expanded?: boolean;
  onExpandToggle?: () => void;
}

/**
 * SubtasksList
 * Manages collection of subtasks with time budget tracking
 * Shows visual progress bar and warnings
 */
const SubtasksList: React.FC<SubtasksListProps> = ({
  subtasks,
  onSubtaskChange,
  onSubtaskAdd,
  onSubtaskRemove,
  parentEstimatedTime,
  parentDueDate,
  parentStartDate,
  availableUsers,
  errors = {},
  expanded = true,
  onExpandToggle,
}) => {
  // Calculate time budget
  const totalSubtaskTime = subtasks.reduce((sum, st) => sum + (st.estimatedTime || 0), 0);
  const remainingTime = parentEstimatedTime - totalSubtaskTime;
  const usagePercent = Math.min((totalSubtaskTime / parentEstimatedTime) * 100, 100);
  const isOverBudget = totalSubtaskTime > parentEstimatedTime;

  const canAddMore = availableUsers.length > 0 && !isOverBudget;

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header with Toggle */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          cursor: onExpandToggle ? 'pointer' : 'default',
        }}
        onClick={onExpandToggle}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Subtasks ({subtasks.length}) - Optional
          </Typography>
          {onExpandToggle && (
            <IconButton size="small">
              {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </IconButton>
          )}
        </Box>
        {subtasks.length > 0 && (
          <Typography
            variant="caption"
            color={isOverBudget ? 'error.main' : 'text.secondary'}
            fontWeight={500}
          >
            {totalSubtaskTime} / {parentEstimatedTime} min
          </Typography>
        )}
      </Box>

      <Collapse in={expanded}>
        {/* Time Budget Indicator */}
        {subtasks.length > 0 && (
          <Box
            sx={{
              p: 2,
              bgcolor: isOverBudget ? 'error.lighter' : 'grey.50',
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Time Budget Allocation
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={isOverBudget ? 'error.main' : usagePercent > 90 ? 'warning.main' : 'success.main'}
              >
                {formatPercent(usagePercent)} used
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(usagePercent, 100)}
              color={isOverBudget ? 'error' : usagePercent > 90 ? 'warning' : 'success'}
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {remainingTime >= 0 ? `${remainingTime} minutes remaining` : `Over budget by ${Math.abs(remainingTime)} minutes`}
              </Typography>
              {usagePercent > 90 && !isOverBudget && (
                <Typography variant="caption" color="warning.main">
                  ⚠️ Nearly full
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Over Budget Warning */}
        {isOverBudget && (
          <Alert severity="error" icon={<AlertTriangle size={18} />} sx={{ mb: 2 }}>
            <Typography variant="body2">
              Subtasks exceed parent task time by <strong>{Math.abs(remainingTime)} minutes</strong>.
              Adjust subtask times or increase parent task estimated time.
            </Typography>
          </Alert>
        )}

        {/* No Users Warning */}
        {availableUsers.length === 0 && subtasks.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Please assign RACI roles to the parent task first. Subtasks inherit from parent RACI pool.
            </Typography>
          </Alert>
        )}

        {/* Subtasks List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          {subtasks.map((subtask, index) => {
            // Calculate remaining time for this specific subtask
            const timeUsedByOthers = subtasks
              .filter((_, i) => i !== index)
              .reduce((sum, st) => sum + (st.estimatedTime || 0), 0);
            const availableForThis = parentEstimatedTime - timeUsedByOthers - (subtask.estimatedTime || 0);

            return (
              <SubtaskForm
                key={index}
                index={index}
                data={subtask}
                onChange={onSubtaskChange}
                onRemove={onSubtaskRemove}
                availableUsers={availableUsers}
                parentDueDate={parentDueDate}
                parentStartDate={parentStartDate}
                remainingTime={availableForThis}
                errors={errors[index]}
              />
            );
          })}
        </Box>

        {/* Add Subtask Button */}
        <Button
          variant="outlined"
          startIcon={<Plus size={16} />}
          onClick={onSubtaskAdd}
          disabled={!canAddMore}
          fullWidth
          sx={{ borderStyle: 'dashed' }}
        >
          Add Subtask
          {availableUsers.length === 0 && ' (set parent RACI first)'}
          {isOverBudget && ' (over budget)'}
        </Button>

        {subtasks.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
            Subtasks are optional. They help break down complex tasks into smaller pieces.
          </Typography>
        )}
      </Collapse>
    </Box>
  );
};

export default SubtasksList;
